import * as React from 'react';
import type { GlobalProps } from '@bloomberg/media-ui-core';
import { classes, useSlug } from '@bloomberg/media-ui-core';

import { USER_OPTIONS_MENU_ITEM_SIGN_OUT } from '../constants/constants';
import { THEMES, UserOptionsMenuItems } from '../types';

import styles from './UserOptionsDropdown.module.css';

export type UserOptionsDropdownProps = GlobalProps & {
  sections: UserOptionsMenuItems;
  onChange?: React.FormEvent<HTMLInputElement>;
  onSelect: (value: string) => void;
  onClose: () => void;
  onSignOut: () => void;
  theme?: THEMES;
};

type DropdownOptionProps = {
  text: string;
  url: string;
  badge?: string;
  onSelect: (value: string) => void;
  onSignOut: () => void;
};

const DropdownOption = ({
  text,
  url,
  badge,
  onSelect,
  onSignOut,
}: DropdownOptionProps) => {
  if (url === USER_OPTIONS_MENU_ITEM_SIGN_OUT.url) {
    return (
      <button
        className={classes([styles.optionText, styles.signOut])}
        key={text}
        onClick={onSignOut}
      >
        <p>{text}</p>
      </button>
    );
  }

  return (
    <a
      href={url}
      className={styles.optionText}
      key={text}
      onClick={() => onSelect(url)}
    >
      <p>
        {text}
        {badge && <span className={styles.badge}>{badge}</span>}
      </p>
    </a>
  );
};

export const UserOptionsDropdown = React.forwardRef<
  HTMLDivElement,
  UserOptionsDropdownProps
>(function DropdownComponent(props: UserOptionsDropdownProps, ref) {
  const { sections, theme, onSelect, onSignOut } = props;

  const componentName = useSlug('user-options-dropdown');

  const cx = classes(
    [
      styles.userOptionsDropdownContainer,
      {
        [styles.light]: theme === THEMES.LIGHT,
        [styles.dark]: theme === THEMES.DARK,
        [styles.blue]: theme === THEMES.BLUE,
      },
    ],
    {
      ...props,
    }
  );

  return (
    <div
      className={cx}
      ref={ref}
      data-component={componentName}
      data-testid="user-options-dropdown"
    >
      {Object.entries(sections).map(
        ([key, { text: sectionHeaderText, items }]) => {
          return (
            <div className={styles.option} key={key}>
              {sectionHeaderText && <h2>{sectionHeaderText}</h2>}
              {items.map(({ text, url, badge }) => (
                <DropdownOption
                  key={text}
                  text={text}
                  url={url}
                  badge={badge}
                  onSelect={onSelect}
                  onSignOut={onSignOut}
                />
              ))}
            </div>
          );
        }
      )}
    </div>
  );
});
